export type PersistedRoleViewValue = "all" | `${number}`

export interface RoleViewPreferenceV2 {
  version: 2
  scopeId: string
  corpUserId: number
  roleView: PersistedRoleViewValue
}

const ROLE_VIEW_PREFERENCE_VERSION = 2
const ROLE_VIEW_PREFERENCE_PREFIX = "teashop-role-view-v2"

function preferenceKey(scopeId: string, corpUserId: number) {
  return `${ROLE_VIEW_PREFERENCE_PREFIX}:${scopeId}:${corpUserId}`
}

function isValidCorpUserId(value: number) {
  return Number.isSafeInteger(value) && value > 0
}

function isValidRoleView(value: unknown): value is PersistedRoleViewValue {
  if (value === "all") return true
  if (typeof value !== "string" || !/^\d+$/.test(value)) return false
  const roleId = Number(value)
  return Number.isSafeInteger(roleId) && roleId > 0
}

export function saveRoleViewPreference(
  scopeId: string,
  corpUserId: number,
  roleView: PersistedRoleViewValue,
) {
  if (!scopeId || !isValidCorpUserId(corpUserId) || !isValidRoleView(roleView)) return false
  const preference: RoleViewPreferenceV2 = {
    version: ROLE_VIEW_PREFERENCE_VERSION,
    scopeId,
    corpUserId,
    roleView,
  }
  try {
    localStorage.setItem(preferenceKey(scopeId, corpUserId), JSON.stringify(preference))
    return true
  } catch {
    return false
  }
}

export function readRoleViewPreference(
  scopeId: string,
  corpUserId?: number,
): RoleViewPreferenceV2 | undefined {
  // A scope is shared by multiple accounts on the same browser. Reading without
  // an explicit principal can therefore restore another account's role.
  if (!scopeId || corpUserId === undefined || !isValidCorpUserId(corpUserId)) return undefined

  try {
    const key = preferenceKey(scopeId, corpUserId)
    const raw = localStorage.getItem(key)
    if (!raw) return undefined
    const preference = JSON.parse(raw) as Partial<RoleViewPreferenceV2>
    if (
      preference.version !== ROLE_VIEW_PREFERENCE_VERSION
      || preference.scopeId !== scopeId
      || preference.corpUserId !== corpUserId
      || !isValidRoleView(preference.roleView)
    ) {
      throw new Error("invalid role view preference")
    }
    return preference as RoleViewPreferenceV2
  } catch {
    try {
      localStorage.removeItem(preferenceKey(scopeId, corpUserId))
    } catch {
      // Storage can be unavailable; runtime state still falls back safely.
    }
    return undefined
  }
}

export function clearRoleViewPreference(scopeId: string, corpUserId?: number) {
  if (!scopeId || corpUserId === undefined || !isValidCorpUserId(corpUserId)) return
  try {
    localStorage.removeItem(preferenceKey(scopeId, corpUserId))
  } catch {
    // Best effort cleanup; callers still reset the runtime role.
  }
}
